/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useCallback, useState, useRef } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import {
  ColDef,
  GridApi,
  PinnedRowDataChangedEvent,
  SelectionChangedEvent,
  ModuleRegistry,
  IMultiFilterParams,
  IDateFilterParams,
  ISetFilterParams,
  GridReadyEvent,
  HeaderValueGetterParams,
} from "@ag-grid-community/core";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { SideBarModule } from "@ag-grid-enterprise/side-bar";
import { MultiFilterModule } from "@ag-grid-enterprise/multi-filter";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { LicenseManager } from "@ag-grid-enterprise/core";

import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DownloadIcon from "@mui/icons-material/Download";

import { generateClient } from "aws-amplify/api";

import {
  updateTeam,
  updateTeamUserMap,
  createUser,
  updateTeamReview,
} from "../graphql/mutations";
import { computeSpRoas } from "./common/Metrics";
import {
  listWeblabs,
  getMetadata,
  getTeams,
  getTeamReviewByTeamId,
} from "../graphql/queries";
import {
  type Metadata,
  type ListTeamWeblabsInput,
  type TeamWeblab,
  type WeblabRowType,
  type Configs,
  type GetTeamPreferencesInput,
  type UpdateTeamInput,
  type TeamSettings,
  type GetUsersByTeamIdInput,
  type UpdateTeamUserMapInput,
  type Team,
  type Users,
  type CreateUserInput,
  type User,
  type ListWeblabsByTeamInput,
  type GetTeamReviewByTeamIdInput,
  type UpdateTeamReviewInput,
} from "../models/API";
import { Resizable } from "re-resizable";
import {
  covertUtcToLocal,
  currencyFormatter,
  numberFormatter,
} from "./utils/formatters";
import DetailsPanel from "./DetailsPanel";
import ErrorModal, { getErrorMessage } from "./ErrorModal";
import MessageModal from "./MessageModal";
import HelpCenter from "./HelpCenter";
import AdminDialog from "./AdminDialog";
import DetailsPanelSummary from "./DetailsPanelSummary";
import { FlagCellRenderer } from "./utils/FlagIcon";
import { StatusCellRenderer } from "./utils/StatusIcon";
import DetailsPanelWeblab from "./DetailsPanelWeblab";
import DetailsPanelWeblabTreatment from "./DetailsPanelWeblabTreatment";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-062883}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Amazon}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Advertising_Weblab_Tracker}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Advertising_Weblab_Tracker}_need_to_be_licensed___{Advertising_Weblab_Tracker}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{31_August_2025}____[v3]_[01]_MTc1NjU5NDgwMDAwMA==d0ae68cdf8c6485d08f01bf537d9a257"
);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  SetFilterModule,
  SideBarModule,
  MultiFilterModule,
  ExcelExportModule,
  RangeSelectionModule,
]);

const client = generateClient();

interface ReviewGridComponentProps {
  isOpen: boolean;
  currentUserName: string;
  userConfig: User | null;
  currentTeamId: number;
  teamConfig: Team;
}

function reviewColumnsTrim(columns: ColDef[]): ColDef[] {
  columns.forEach((element, index, arr) => {
    if (
      element.field === "name" ||
      element.field === "control" ||
      element.field === "treatment"
    ) {
      element.rowGroup = true;
      arr[index] = element;
    }
  });
  return columns;
}

// @ts-ignore
const ReviewGridComponent: React.FC<ReviewGridComponentProps> = ({
  isOpen,
  currentUserName,
  userConfig,
  currentTeamId,
  teamConfig,
}) => {
  const [error, setError] = useState<any>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [rowData, setRowData] = useState<WeblabRowType[]>([]);
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number>(0);
  const [configs, setConfigs] = useState<Configs>({
    usersDict: {},
    userIDsDict: {},
    pageTypesDict: {},
    teamsDict: {},
    teamsCustomDict: {},
    teamReviewsCustomDict: {},
    teamsUsersDict: {},
    teamsPendingUsersDict: {},
    usersTeamsDict: {},
    deviceTypesDict: {},
    experimentTypesDict: {},
    marketplacesDict: {},
    marketplaceIdsDict: {},
    marketplaceDisplayOrderDict: {},
    regionDict: {},
    statusesDict: {},
    statusIDsDict: {},
  });
  const [refreshFlag, setRefresh] = useState(false);
  const [metaRefreshFlag, setMetaRefresh] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  let [editingRow, setEditingRow] = useState(null);
  let [showWatchList, setShowWatchList] = useState(false);
  let [showHelp, setShowHelp] = useState(
    userConfig?.login_times === undefined || userConfig?.login_times < 10
  );
  const [selectedRowData, setSelectedRowData] = useState<WeblabRowType | null>(
    null
  );
  const [selectedGroupRowData, setSelectedGroupRowData] =
    useState<WeblabRowType | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [selectedGroupChildrenRows, setSelectedGroupChildrenRows] = useState<
    any[] | null
  >([]);
  const [selectedGroupParentKey, setSelectedGroupParentKey] = useState<
    string | null
  >(null);
  const gridRef = useRef<GridApi | null>(null);
  const isFirstRender = useRef(true);
  const isUpdatedByForm = useRef(false);
  const teamCustomAttributes = JSON.parse(
    teamConfig?.custom_attributes || "{}"
  );
  let [showCurrentYear, setShowCurrentYear] = useState<boolean>(true);
  const userAttributes = JSON.parse(userConfig?.attributes || "{}");
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Define the column definitions
  const initialColumnDefs: ColDef[] = [
    {
      headerName: "All",
      field: "all",
      width: 100,
      rowGroup: true,
      rowGroupIndex: 0,
      hide: true,
    },
    {
      headerName: "Weblab ID",
      field: "name",
      sortable: true,
      filter: true,
      width: 300,
      rowGroup: true,
      rowGroupIndex: 1,
      hide: true,
    },
    {
      headerName: "Treatment",
      field: "control_treatment",
      sortable: true,
      filter: true,
      rowGroup: true,
      rowGroupIndex: 2,
      hide: true,
    },
    {
      headerName: "ID",
      width: 80,
      field: "id",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "",
      field: "team_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Sub Team",
      field: "weblab_team",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Owners",
      field: "owners",
      sortable: true,
      filter: true,
      width: 220,
    },
    {
      headerName: "Bar Raiser Wiki",
      field: "bar_raiser_wiki",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "weblab_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Short Description",
      field: "description",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Deep Dive Link",
      field: "deep_dive_link",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Global Initiative Name",
      field: "global_init_name",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Experiment Type",
      field: "experiment_type",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "experiment_type_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Marketplace ID",
      field: "marketplace",
      sortable: true,
      filter: true,
      cellRenderer: FlagCellRenderer,
    },
    {
      headerName: "",
      field: "marketplace_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Is Smoke Test",
      field: "smoke_test",
      sortable: true,
      filter: true,
      cellDataType: "boolean",
    },
    {
      headerName: "Baseline Treatment",
      field: "control",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Experiment Treatment",
      field: "treatment",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "start_date",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Experiment Start Date",
      field: "start_date_date",
      sortable: true,
      filter: true,
      cellDataType: "date",
      editable: true,
    },
    {
      headerName: "Analysis Duration",
      field: "analysis_duration",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Page Type",
      field: "page_type",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "page_type_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Gating Weblab",
      field: "is_gating",
      sortable: true,
      filter: true,
      cellDataType: "boolean",
    },
    {
      headerName: "Device",
      field: "device",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "device_type_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      filter: true,
      cellRenderer: StatusCellRenderer,
      editable: true,
    },
    {
      headerName: "",
      field: "status_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "",
      field: "launch_date",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Expected Launch Date",
      field: "launch_date_date",
      sortable: true,
      filter: true,
      cellDataType: "date",
      editable: true,
    },
    {
      headerName: "",
      field: "actual_launch_date",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Actual Launch Date",
      field: "actual_launch_date_date",
      sortable: true,
      filter: true,
      cellDataType: "date",
      editable: true,
    },
    {
      headerName: "Exposure",
      field: "gating_weblab",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Revenue Share(%)",
      field: "rev_share",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Expected Revenue Impact",
      field: "revenue_impact_planned_weblab",
      sortable: true,
      filter: true,
      cellDataType: "number",
      valueFormatter: (params) => currencyFormatter(params.value),
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Irrelevance Rate Control(%)",
      field: "irrelevance_rate_control",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Irrelevance Rate Treatment(%)",
      field: "irrelevance_rate_treatment",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Metrics Manually Supplied",
      field: "is_manual_weblab",
      sortable: true,
      filter: true,
      cellDataType: "boolean",
    },
    {
      headerName: "",
      field: "analysis_start_date",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Analysis Start Date",
      field: "analysis_start_date_date",
      sortable: true,
      filter: true,
      cellDataType: "date",
    },
    {
      headerName: "",
      field: "analysis_end_date",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Analysis End Date",
      field: "analysis_end_date_date",
      sortable: true,
      filter: true,
      cellDataType: "date",
    },
    {
      headerName: "APT Job IDs",
      field: "job_ids",
      sortable: true,
      filter: true,
    },
    {
      headerName: "pIrrel Human Relevance Audit",
      field: "pirrel_human_relevance_audit",
      sortable: true,
      filter: true,
    },
    {
      headerName: "CTR",
      field: "ctr",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Coverage",
      field: "coverage",
      sortable: true,
      filter: true,
    },
    {
      headerName: "CPC",
      field: "cpc",
      sortable: true,
      filter: true,
    },
    {
      headerName: "ACOS",
      field: "acos",
      sortable: true,
      filter: true,
    },
    {
      headerName: "SP Revenue: probability",
      field: "sp_revenue_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Revenue: lift(%)",
      field: "sp_revenue_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Revenue: impact",
      field: "sp_revenue_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "OPS: probability",
      field: "ops_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "OPS: lift(%)",
      field: "ops_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "OPS: impact",
      field: "ops_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "GCCP: probability",
      field: "gccp_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "GCCP: lift(%)",
      field: "gccp_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "GCCP: impact",
      field: "gccp_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "Net CP: probability",
      field: "net_cp_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Net CP: lift(%)",
      field: "net_cp_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Net CP: impact",
      field: "net_cp_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "SP Clicks: probability",
      field: "sp_clicks_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Clicks: lift(%)",
      field: "sp_clicks_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Clicks: impact",
      field: "sp_clicks_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => numberFormatter(params.value),
    },
    {
      headerName: "Paid Units: probability",
      field: "paid_units_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Paid Units: lift(%)",
      field: "paid_units_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "Paid Units: impact",
      field: "paid_units_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => numberFormatter(params.value),
    },
    {
      headerName: "CSales: probability",
      field: "csales_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "CSales: lift(%)",
      field: "csales_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "CSales: impact",
      field: "csales_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "SP Attr. OPS (1-day): probability",
      field: "sp_attributed_ops_1day_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Attr. OPS (1-day): lift(%)",
      field: "sp_attributed_ops_1day_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP Attr. OPS (1-day): impact",
      field: "sp_attributed_ops_1day",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "SP ROAS (1-day): probability",
      field: "sp_roas_1day_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP ROAS (1-day): lift(%)",
      field: "sp_roas_1day_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SP ROAS (1-day): impact",
      field: "sp_roas_1day",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return params.value.toFixed(4);
      },
    },
    {
      headerName: "SB Revenue: probability",
      field: "sb_revenue_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SB Revenue: lift(%)",
      field: "sb_revenue_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SB Revenue: impact",
      field: "sb_revenue_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: "SB Clicks: probability",
      field: "sb_clicks_prob",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SB Clicks: lift(%)",
      field: "sb_clicks_lift",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
    },
    {
      headerName: "SB Clicks: impact",
      field: "sb_clicks_impact",
      sortable: true,
      filter: true,
      cellDataType: "number",
      cellRenderer: (params: any) => {
        if (params.node.group) {
          return "";
        }
        return params.value;
      },
      valueFormatter: (params) => numberFormatter(params.value),
    },
    {
      headerName: "Shared Weblab",
      field: "is_shared_weblab",
      sortable: true,
      filter: true,
      cellDataType: "boolean",
    },
    {
      headerName: "Creator",
      field: "user_name",
      sortable: true,
      filter: true,
    },
    {
      headerName: "",
      field: "user_id",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "",
      field: "last_updated_by",
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      headerName: "Last Updated By",
      field: "last_updated_by_user_name",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Last Updated Time",
      field: "updated_ts",
      sortable: true,
      filter: true,
    },
    {
      headerName: "Comments",
      field: "comments",
      width: 200,
      sortable: true,
      filter: "agTextColumnFilter",
      editable: true,
    },
  ];
  const [columnDefs, setColumnsDefs] = useState(initialColumnDefs);
  const [showTeamSettingsDialog, setShowTeamSettingsDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [weblabIds, setWeblabIds] = useState<number[]>([]);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showRequestAccessDialog, setShowRequestAccessDialog] = useState(false);
  const [teamSettings, setTeamSettings] = useState<TeamSettings>({
    id: 0,
    name: "",
    custom_attributes: JSON.parse("{}"),
  });
  const [usersSettings, setUsersSettings] = useState<Users>({
    team_id: 0,
    current_user_id: 0,
    user_ids: [],
    pending_user_ids: [],
  });
  const gridOptions: any = {
    defaultColDef: {
      resizable: true,
      wrapHeaderText: teamCustomAttributes.is_wrap_header_text,
      autoHeaderHeight: teamCustomAttributes.is_auto_header_height,
      wrapText: teamCustomAttributes.is_wrap_text,
      autoHeight: teamCustomAttributes.is_auto_height,
      cellDataType: false,
    },
    sideBar: {
      toolPanels: [
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {},
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: ["filters"],
    },
    allowDragFromColumnsToolPanel: true,
    enableRangeSelection: true,
    enableFillHandle: true,
    fillHandleDirection: "y",
    rowHeight: 30,
    autoAggFunc: false,
  };

  useEffect(() => {
    if (currentTeamId > 0) {
      console.log("Team: [" + currentTeamId + "] meta is refreshing...");
      fetchMetadata().then((r) => {
        refresh();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId, metaRefreshFlag]);

  useEffect(() => {
    if (currentTeamId > 0 && teamConfig && teamConfig.custom_attributes) {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      console.log(
        "Team: [" +
          currentTeamId +
          "] data is refreshing with current year flag [" +
          showCurrentYear +
          "], show watch list [" +
          showWatchList +
          "], refresh flag [" +
          refreshFlag +
          "], is open[" +
          isOpen +
          "]"
      );
      fetchWeblabs().then((r) => {
        setTimeout(() => {
          if (selectedRowData) {
            selectRow(selectedRowData?.id);
          }
        }, 500);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCurrentYear, showWatchList, refreshFlag, isOpen]);

  const getColumnState = async () => {
    let columnState = [];
    if (currentTeamId > 0) {
      try {
        const getTeamReviewByTeamIdInput: GetTeamReviewByTeamIdInput = {
          team_id: currentTeamId,
        };
        const teamData = await client.graphql({
          query: getTeamReviewByTeamId,
          variables: {
            input: getTeamReviewByTeamIdInput,
          },
        });
        // @ts-ignore
        if (teamData.data.getTeamReviewByTeamId.custom_attributes) {
          const customAttributes = JSON.parse(
            teamData.data.getTeamReviewByTeamId.custom_attributes
          );
          if (customAttributes.hasOwnProperty("column_state")) {
            columnState = customAttributes.column_state;
          }
        }
      } catch (error) {
        console.error(error);
        setError(error);
      }
    }
    return columnState;
  };

  const onGridReady = useCallback((params: GridReadyEvent) => {
    console.log("in onGridReady");
    gridRef.current = params.api;
    params.api.deselectAll(); // Deselect rows
    params.api.setFilterModel(null); // Reset all filters
    setSelectedRowData(null);
  }, []);

  const onRowDoubleClicked = (event: any) => {
    setEditingRow(event.data);
    setIsModalOpen(true);
  };

  const openAdminDialog = async () => {
    await fetchTeamsData();
    let filteredRowData: WeblabRowType[] = [];
    gridRef.current?.forEachNodeAfterFilter((node) => {
      filteredRowData.push(node.data);
    });
    const ids = filteredRowData.map((item) => item.id);
    setWeblabIds(ids);
    setShowAdminDialog(true);
  };

  const openUsersDialog = () => {
    setShowUsersDialog(true);
  };

  async function fetchTeamsData() {
    try {
      const teamsData = await client.graphql({
        query: getTeams,
      });
      const teams = teamsData.data.getTeams;
      setTeams(teams);

      const createUserInput: CreateUserInput = {
        user_name: currentUserName,
      };
      const userData = await client.graphql({
        query: createUser,
        variables: {
          input: createUserInput,
        },
      });
      const currentUserId = userData.data.createUser.id;
      setCurrentUserId(currentUserId);
    } catch (err) {
      console.error(err);
    }
  }

  const openRequestAccessDialog = async () => {
    await fetchTeamsData();
    setShowRequestAccessDialog(true);
  };

  const setRegion = () => {
    setRefresh(!refreshFlag);
  };

  const refresh = () => {
    setRefresh(!refreshFlag);
  };

  const metaRefresh = () => {
    setMetaRefresh(!metaRefreshFlag);
  };
  const selectRow = (id: any) => {
    setTimeout(() => {
      if (gridRef.current?.isDestroyed() === false) {
        gridRef.current?.forEachNode(function (node) {
          if (node.data.id === id) {
            node.setSelected(true);
          }
        });
      }
    }, 500);
  };

  const handleTeamSettingsClose = () => {
    setShowTeamSettingsDialog(false);
  };
  const handleAdminClose = () => {
    refresh();
    setShowAdminDialog(false);
  };
  const handleUsersClose = () => {
    metaRefresh();
    setShowUsersDialog(false);
  };

  const handleRequestAccessClose = () => {
    setShowRequestAccessDialog(false);
  };
  async function fetchWeblabs() {
    setMessage("Please wait, refreshing...");
    try {
      if (configs.teamsCustomDict[currentTeamId]) {
        let allColumns = initialColumnDefs;
        // @ts-ignore
        configs.teamsCustomDict[currentTeamId].columns.forEach((element) => {
          // @ts-ignore
          const column = {
            headerName: element.label,
            field: "additional_columns." + element.col_name,
            sortable: true,
            filter: true,
            editable: true,
            cellDataType: element.type.toLowerCase(),
          };
          allColumns.splice(-5, 0, column); // keep favorite, delete, edit, and comments at last elements
        });
        await sortColumnsDefs(allColumns);
        const getTeamPreferencesInput: GetTeamPreferencesInput = {
          team_id: currentTeamId,
        };

        let weblabs: WeblabRowType[] = [];
        let currentWeblabs: WeblabRowType[] = [];
        let offset: number = 0;
        const LIMIT: number = 800;
        do {
          currentWeblabs = [];
          const listTeamWeblabsInput: ListTeamWeblabsInput = {
            team_id: currentTeamId,
            limit: LIMIT,
            offset: offset,
            is_current_year: showCurrentYear,
          };
          const listWeblabsByTeamInput: ListWeblabsByTeamInput = {
            team_id: currentTeamId,
          };
          const weblabData = await client.graphql({
            query: listWeblabs,
            variables: {
              input: getTeamPreferencesInput,
              teamInput: listTeamWeblabsInput,
              weblabInput: listWeblabsByTeamInput,
            },
          });
          currentWeblabs = weblabData.data.listTeamWeblabs.map(
            (teamWeblab: TeamWeblab) => {
              let teamPreference = null;
              if (weblabData.data.getTeamPreferences != null) {
                teamPreference = weblabData.data.getTeamPreferences.filter(
                  (item) =>
                    item.team_weblab_id === teamWeblab.id &&
                    item.team_id === teamWeblab.team_id
                )[0];
              }
              const weblab = weblabData.data.listWeblabsByTeam.filter(
                (item) => item.weblab_id === teamWeblab.weblab_id
              )[0];

              return {
                all: "ALL",
                id: teamWeblab.id,
                team_id: teamWeblab.team_id,
                weblab_team: weblab.sub_team,
                user_name: configs.usersDict[teamWeblab.user_id],
                user_id: teamWeblab.user_id,
                bar_raiser_wiki: weblab.bar_raiser_wiki!,
                name: weblab.name,
                weblab_id: weblab.weblab_id,
                is_shared_weblab: weblab.shared_flag,
                description: weblab.description,
                owners: weblab.owners,
                global_init_name: weblab.global_init_name!,
                deep_dive_link: weblab.deep_dive_link!,
                experiment_type:
                  configs.experimentTypesDict[weblab.experiment_type_id],
                experiment_type_id: weblab.experiment_type_id,
                marketplace:
                  configs.marketplacesDict[teamWeblab.marketplace_id],
                marketplace_id: teamWeblab.marketplace_id,
                smoke_test: teamWeblab.smoke_test!,
                control_treatment:
                  teamWeblab.control! + ":" + teamWeblab.treatment!,
                control: teamWeblab.control!,
                treatment: teamWeblab.treatment!,
                start_date: teamWeblab.start_date,
                start_date_date:
                  teamWeblab.start_date !== "" && teamWeblab.start_date !== null
                    ? new Date(teamWeblab.start_date)
                    : null,
                analysis_duration: teamWeblab.analysis_duration,
                page_type: configs.pageTypesDict[weblab.page_type_id],
                page_type_id: weblab.page_type_id,
                is_gating: teamWeblab.is_gating,
                device: configs.deviceTypesDict[teamWeblab.device_type_id],
                device_type_id: teamWeblab.device_type_id,
                status: configs.statusesDict[teamWeblab.weblab_status_id],
                status_id: teamWeblab.weblab_status_id,
                launch_date: teamWeblab.launch_date,
                launch_date_date:
                  teamWeblab.launch_date !== "" &&
                  teamWeblab.launch_date !== null
                    ? new Date(teamWeblab.launch_date)
                    : null,
                actual_launch_date: teamWeblab.actual_launch_date,
                actual_launch_date_date:
                  teamWeblab.actual_launch_date !== "" &&
                  teamWeblab.actual_launch_date !== null
                    ? new Date(teamWeblab.actual_launch_date)
                    : null,
                gccp_impact: teamWeblab.gccp_impact,
                metrics_sync_status: teamWeblab.metrics_sync_status,
                metrics_sync_attempts: teamWeblab.metrics_sync_attempts,
                metrics_next_retry_at: teamWeblab.metrics_next_retry_at,
                metrics_last_error: teamWeblab.metrics_last_error,
                metrics_synced_at: teamWeblab.metrics_synced_at,
                gccp_lift: teamWeblab.gccp_lift,
                gccp_prob: teamWeblab.gccp_prob,
                net_cp_impact: teamWeblab.net_cp_impact,
                net_cp_lift: teamWeblab.net_cp_lift,
                net_cp_prob: teamWeblab.net_cp_prob,
                ops_impact: teamWeblab.ops_impact,
                ops_lift: teamWeblab.ops_lift,
                ops_prob: teamWeblab.ops_prob,
                sp_clicks_impact: teamWeblab.sp_clicks_impact,
                sp_clicks_lift: teamWeblab.sp_clicks_lift,
                sp_clicks_prob: teamWeblab.sp_clicks_prob,
                sp_revenue_impact: teamWeblab.sp_revenue_impact,
                sp_revenue_lift: teamWeblab.sp_revenue_lift,
                sp_revenue_prob: teamWeblab.sp_revenue_prob,
                paid_units_impact: teamWeblab.paid_units_impact,
                paid_units_lift: teamWeblab.paid_units_lift,
                paid_units_prob: teamWeblab.paid_units_prob,
                csales_impact: teamWeblab.csales_impact,
                csales_lift: teamWeblab.csales_lift,
                csales_prob: teamWeblab.csales_prob,
                sb_revenue_impact: teamWeblab.sb_revenue_impact,
                sb_revenue_lift: teamWeblab.sb_revenue_lift,
                sb_revenue_prob: teamWeblab.sb_revenue_prob,
                sb_clicks_impact: teamWeblab.sb_clicks_impact,
                sb_clicks_lift: teamWeblab.sb_clicks_lift,
                sb_clicks_prob: teamWeblab.sb_clicks_prob,
                sp_attributed_ops_1day: teamWeblab.sp_attributed_ops_1day,
                sp_attributed_ops_1day_prob:
                  teamWeblab.sp_attributed_ops_1day_prob,
                sp_attributed_ops_1day_lift:
                  teamWeblab.sp_attributed_ops_1day_lift,
                sp_roas_1day_prob: teamWeblab.sp_roas_1day_prob,
                sp_roas_1day_lift: teamWeblab.sp_roas_1day_lift,
                sp_roas_1day:
                  teamWeblab.sp_roas_1day ??
                  computeSpRoas(
                    teamWeblab.sp_attributed_ops_1day,
                    teamWeblab.sp_revenue_impact
                  ),
                acos: teamWeblab.acos,
                analysis_end_date: teamWeblab.analysis_end_date,
                analysis_end_date_date:
                  teamWeblab.analysis_end_date !== "" &&
                  teamWeblab.analysis_end_date !== null
                    ? new Date(teamWeblab.analysis_end_date)
                    : null,
                analysis_start_date: teamWeblab.analysis_start_date,
                analysis_start_date_date:
                  teamWeblab.analysis_start_date !== "" &&
                  teamWeblab.analysis_start_date !== null
                    ? new Date(teamWeblab.analysis_start_date)
                    : null,
                coverage: teamWeblab.coverage,
                cpc: teamWeblab.cpc,
                ctr: teamWeblab.ctr,
                gating_weblab: teamWeblab.gating_weblab,
                irrelevance_rate_control: teamWeblab.irrelevance_rate_control,
                irrelevance_rate_treatment:
                  teamWeblab.irrelevance_rate_treatment,
                is_chosen_baseline_treatment:
                  teamWeblab.is_chosen_baseline_treatment,
                is_paste_manual_reports: teamWeblab.is_paste_manual_reports,
                job_ids: teamWeblab.job_ids,
                pirrel_human_relevance_audit:
                  teamWeblab.pirrel_human_relevance_audit,
                rev_share: teamWeblab.rev_share,
                revenue_impact_planned_weblab:
                  teamWeblab.revenue_impact_planned_weblab,
                is_manual_weblab: teamWeblab.is_manual_weblab,
                team_preference_id: teamPreference?.id || null,
                additional_columns: JSON.parse(
                  teamPreference?.additional_columns || "{}"
                ),
                favorite: teamPreference?.watch_flag || false,
                comments: teamPreference?.comments,
                last_updated_by: teamWeblab.last_updated_by,
                last_updated_by_user_name:
                  configs.usersDict[teamWeblab.last_updated_by],
                updated_ts: covertUtcToLocal(teamWeblab.updated_ts),
                created_ts: teamWeblab.created_ts,
              };
            }
          );
          weblabs.push(...currentWeblabs);
          offset += currentWeblabs.length;
        } while (currentWeblabs.length === LIMIT);
        if (showWatchList) {
          // TODO: we can get data from API rather than filter on frontend for better performance
          setRowData(weblabs.filter((item) => item.favorite));
        } else {
          setRowData(weblabs);
        }
      }
    } catch (err) {
      console.error(err);
      setError(err);
    }
    setMessage(null);
  }

  function resetFilters() {
    gridRef.current!.setFilterModel(null);
    console.log("The filters have been reset");
  }

  function exportData() {
    gridRef.current!.exportDataAsExcel();
    console.log("The team data has been exported");
  }
  function getDate(value: string): Date {
    let dateParts = value.split("/");
    return new Date(
      Number(dateParts[0]),
      Number(dateParts[1]) - 1,
      Number(dateParts[2])
    );
  }

  const dateFilterParams: IMultiFilterParams = {
    filters: [
      {
        filter: "agDateColumnFilter",
        filterParams: {
          convertValuesToStrings: true,
          comparator: (filterDate: Date, cellValue: Date) => {
            if (cellValue == null) return -1;
            return cellValue.getTime() - filterDate.getTime();
          },
        } as IDateFilterParams,
      },
      {
        filter: "agSetColumnFilter",
        filterParams: {
          convertValuesToStrings: true,
          comparator: (a: string, b: string) => {
            if (a === null || b === null) return 0;
            return getDate(a).getTime() - getDate(b).getTime();
          },
        } as ISetFilterParams,
      },
    ],
  };

  function applyFilters(columns: ColDef[]) {
    columns.forEach((element: any, index: number) => {
      if (element.filter === true) {
        //element.floatingFilter = true;
        switch (element.cellDataType) {
          case "number":
            element.filter = "agMultiColumnFilter";
            element.filterParams = {
              filters: [
                {
                  filter: "agNumberColumnFilter",
                },
                {
                  filter: "agSetColumnFilter",
                },
              ],
            } as IMultiFilterParams;
            break;
          case "date":
            element.filter = "agMultiColumnFilter";
            element.filterParams = dateFilterParams;
            break;
          default:
            element.filter = "agMultiColumnFilter";
        }
      }
      if (
        element.hide === true &&
        (element.headerName === "" || element.rowGroup === true)
      ) {
        element.suppressColumnsToolPanel = true;
      } else {
        if (element.cellDataType === "number") {
          element.enableValue = true;
          element.aggFunc = "sum";
        } else {
          element.enableRowGroup = true;
        }
      }
      // @ts-ignore
      element.wrapText = teamSettings.custom_attributes.is_wrap_text;
      element.wrapHeaderText =
        // @ts-ignore
        teamSettings.custom_attributes.is_wrap_header_text;
      // @ts-ignore
      element.autoHeight = teamSettings.custom_attributes.is_auto_height;
      element.autoHeaderHeight =
        // @ts-ignore
        teamSettings.custom_attributes.is_auto_header_height;
    });
  }
  async function sortColumnsDefs(columns: ColDef[]) {
    type OrderMap = {
      [key: string]: [number, number, boolean];
    };
    let orderMap: OrderMap = {};
    let columnState = await getColumnState();
    if (columnState && columnState.length > 1) {
      columnState.forEach((element: any, index: number) => {
        orderMap[element.colId] = [index, element.width, element.hide];
      });
      columns.sort((a, b) => {
        a.width = a.field! in orderMap ? orderMap[a.field!][1] : a.width;
        a.hide = a.field! in orderMap ? orderMap[a.field!][2] : a.hide;
        b.width = b.field! in orderMap ? orderMap[b.field!][1] : b.width;
        b.hide = b.field! in orderMap ? orderMap[b.field!][2] : b.hide;
        const orderA =
          a.field! in orderMap ? orderMap[a.field!][0] : Number.MAX_VALUE;
        const orderB =
          b.field! in orderMap ? orderMap[b.field!][0] : Number.MAX_VALUE;
        return orderA - orderB;
      });
    }
    applyFilters(columns);
    setColumnsDefs(columns);
  }

  async function fetchMetadata() {
    try {
      const getUsersByTeamIdInput: GetUsersByTeamIdInput = {
        team_id: currentTeamId,
      };
      const metaData = await client.graphql({
        query: getMetadata,
        variables: {
          input: getUsersByTeamIdInput,
        },
      });
      // @ts-ignore
      const meta: Metadata = metaData.data;
      setMetadata(meta);

      // populate users
      meta.getUsers.forEach((item) => {
        configs.usersDict[item.id] = item.user_name;
        configs.userIDsDict[item.user_name] = item.id;
      });
      // populate teams
      meta.getTeams.forEach((item) => {
        configs.teamsDict[item.id] = item.name;
        configs.teamsCustomDict[item.id] = JSON.parse(item.custom_attributes);
      });
      // populate team_review
      meta.getTeamReviews.forEach((item) => {
        configs.teamReviewsCustomDict[item.team_id] = JSON.parse(
          item.custom_attributes
        );
      });
      // populate team_user_map
      configs.teamsUsersDict[currentTeamId] = [];
      configs.usersTeamsDict[configs.userIDsDict[currentUserName]] = [];
      meta.getUsersByTeamId.forEach((item) => {
        configs.teamsUsersDict[currentTeamId].push(item.id);
      });
      configs.teamsPendingUsersDict[currentTeamId] = [];
      meta.getPendingUsersByTeamId.forEach((item) => {
        configs.teamsPendingUsersDict[currentTeamId].push(item.id);
      });
      const teamData: TeamSettings = {
        id: currentTeamId,
        name: configs.teamsDict[currentTeamId],
        custom_attributes: configs.teamsCustomDict[currentTeamId],
      };
      setTeamSettings(teamData);
      setShowCurrentYear(
        // @ts-ignore
        teamData.custom_attributes.is_default_to_current_year_view
      );
      const usersData: Users = {
        team_id: currentTeamId,
        current_user_id: configs.userIDsDict[currentUserName],
        user_ids: configs.teamsUsersDict[currentTeamId],
        pending_user_ids: configs.teamsPendingUsersDict[currentTeamId] || [],
      };
      setUsersSettings(usersData);
      // populate page types
      meta.getPageTypes.forEach((item) => {
        configs.pageTypesDict[item.id] = item.name;
      });
      // populate device types
      meta.getDeviceTypes.forEach((item) => {
        configs.deviceTypesDict[item.id] = item.name;
      });
      // populate experiment types
      meta.getExperimentTypes.forEach((item) => {
        configs.experimentTypesDict[item.id] = item.name;
      });
      // populate weblab statuses
      meta.getWeblabStatuses.forEach((item) => {
        configs.statusesDict[item.id] = item.name;
        configs.statusIDsDict[item.name] = item.id;
      });
      // populate marketplaces
      meta.getMarketplaces.forEach((item) => {
        configs.regionDict[item.region] = [];
      });
      meta.getMarketplaces.forEach((item) => {
        configs.marketplacesDict[item.marketplace_id] = item.marketplace;
        configs.marketplaceIdsDict[item.marketplace] = item.marketplace_id;
        configs.marketplaceDisplayOrderDict[item.marketplace] =
          item.display_order;
        configs.regionDict[item.region].push(item.marketplace);
      });
      setConfigs(configs);
    } catch (err) {
      console.error(err);
      setError(err);
    }
  }

  const handleTeamSettingsSave = async (item: TeamSettings) => {
    try {
      const team: UpdateTeamInput = {
        id: item.id,
        name: item.name,
        custom_attributes: JSON.stringify(item.custom_attributes),
      };
      const teamData = await client.graphql({
        query: updateTeam,
        variables: {
          input: team,
        },
      });
      setInfoMessage(
        "Please refresh your browser for the team setting changes to take effect!"
      );
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const handleUsersSave = async (item: Users) => {
    try {
      const teamUserMap: UpdateTeamUserMapInput = {
        team_id: currentTeamId,
        user_ids: item.user_ids.join(),
      };
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: teamUserMap,
        },
      });
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const handleRequestAccessSave = async (item: Users) => {
    try {
      const teamUserMap: UpdateTeamUserMapInput = {
        team_id: currentTeamId,
        user_ids: item.user_ids.join(),
      };
      await client.graphql({
        query: updateTeamUserMap,
        variables: {
          input: teamUserMap,
        },
      });
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedData = event.api.getSelectedRows();
    setSelectedRowData(selectedData[0] || null);
    setSelectedGroupRowData(null);
    setSelectedGroupKey(null);
    setSelectedGroupParentKey(null);
    const selectedNodes = event.api.getSelectedNodes();
    selectedNodes.forEach((node) => {
      if (node.group && node.aggData) {
        setSelectedGroupRowData(node.aggData || null);
        setSelectedGroupKey(node.key || null);
        setSelectedGroupParentKey(node.parent?.key || null);
        setSelectedGroupChildrenRows(node.childrenAfterFilter || []);
        console.log("Selected Group Parent:", node.parent);
        console.log("Selected Group:", node.key);
      }
    });
  };

  const onPinnedRowDataChanged = (params: PinnedRowDataChangedEvent) => {
    // @ts-ignore
    if (gridRef.current?.getDisplayedRowCount() > 0) {
      gridRef.current?.ensureIndexVisible(0);
    }
  };

  // @ts-ignore
  const onGridPreDestroyed = useCallback(() => {
    console.log("The grid is destroying!");
    setRowData([]);
    setSelectedRowData(null);
  }, []);

  const onFilterChanged = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.deselectAll();
      const firstRowNode = gridRef.current.getDisplayedRowAtIndex(0);
      if (firstRowNode) {
        firstRowNode.setSelected(true);
      }
    }
  }, []);

  async function onColumnChanged(params: any) {
    try {
      let customAttributes = JSON.parse('{"column_state":[]}');
      // @ts-ignore
      customAttributes["column_state"] = gridRef.current!.getColumnState();
      const team: UpdateTeamReviewInput = {
        team_id: currentTeamId,
        custom_attributes: JSON.stringify(customAttributes),
      };
      await client.graphql({
        query: updateTeamReview,
        variables: {
          input: team,
        },
      });
    } catch (error) {
      console.error(error);
      setError(error);
    }
  }

  const onFirstDataRendered = async (params: any) => {
    gridRef.current?.getDisplayedRowAtIndex(0)?.setSelected(true);
    const firstRowNode = gridRef.current?.getDisplayedRowAtIndex(0);
    if (firstRowNode) {
      //selectRow(firstRowNode.data.id);
    }
  };

  if (currentTeamId === 0)
    return (
      isOpen && (
        <div>
          <div>You don't have permission to access any team's data.</div>
          <button onClick={openRequestAccessDialog}>Request Access</button>
        </div>
      )
    );

  function handleGlobalSearch() {
    gridRef.current!.setGridOption(
      "quickFilterText",
      (document.getElementById("global-search-text-box") as HTMLInputElement)
        .value
    );
  }

  function toggleHelpSection() {
    setShowHelp(!showHelp);
  }

  return (
    isOpen && (
      <div
        style={{
          display: "flex",
          height: "100%",
          width: "87.8%",
        }}
      >
        <Resizable
          style={{
            flex: showHelp ? "0 0 80%" : "0 0 100%",
            //flexGrow: showHelp ? "0" : "0",
            //flexShrink: showHelp ? "0" : "0",
            //flexBasis: showHelp ? "80%" : "100%",
          }}
        >
          <Resizable
            defaultSize={{
              width: "100%",
              height: "99.6%",
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              borderStyle: "double",
            }}
          >
            <Resizable
              defaultSize={{
                height: "50%",
                width: "100%",
              }}
              enable={{
                top: true,
                right: false,
                bottom: true,
                left: false,
                topRight: false,
                bottomRight: false,
                bottomLeft: false,
                topLeft: false,
              }}
              style={{
                overflow: "auto",
                background: "#eee",
                border: "1px solid gray",
              }}
            >
              <DetailsPanel selectedRowData={selectedRowData} />
              <DetailsPanelWeblab
                isOpen={selectedGroupParentKey === "ALL"}
                weblabId={selectedGroupKey}
                selectedGroupRowData={selectedGroupRowData}
                selectedGroupChildrenRows={selectedGroupChildrenRows}
              />
              <DetailsPanelWeblabTreatment
                isOpen={
                  selectedGroupParentKey !== "ALL" &&
                  selectedGroupParentKey !== null
                }
                weblabId={selectedGroupParentKey}
                treatment={selectedGroupKey}
                selectedGroupRowData={selectedGroupRowData}
                selectedGroupChildrenRows={selectedGroupChildrenRows}
              />
              <DetailsPanelSummary
                isOpen={
                  selectedGroupParentKey === null && selectedGroupKey !== null
                }
                selectedGroupChildrenRows={selectedGroupChildrenRows}
              />
            </Resizable>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "15%",
                border: "1px solid gray",
                width: "99.6%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                  marginTop: "7px",
                  marginLeft: "5px",
                  marginRight: "5px",
                  height: "25px",
                  width: "99.6%",
                }}
              >
                <Tooltip title="Show Current Year Only" placement={"top"}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showCurrentYear}
                        onChange={(e) => setShowCurrentYear(e.target.checked)}
                        name={"showCurrentYear"}
                        color={"primary"}
                      />
                    }
                    label="Current Year"
                  />
                </Tooltip>
                <Tooltip title="Refresh Data" placement={"top"}>
                  <IconButton onClick={refresh}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear Column Filters" placement={"top"}>
                  <IconButton onClick={resetFilters}>
                    <FilterListOffIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export Data" placement={"top"}>
                  <IconButton onClick={exportData}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Help Center" placement={"top"}>
                  <IconButton
                    color={showHelp ? "primary" : "default"}
                    onClick={toggleHelpSection}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
                <input
                  type={"text"}
                  id="global-search-text-box"
                  onChange={handleGlobalSearch}
                  placeholder={"Search..."}
                  style={{ width: "300px" }}
                />
              </div>
              <div
                className="ag-theme-quartz"
                style={{
                  flex: 1,
                  minHeight: "calc(15% - 25px)",
                  background: "#fff",
                  overflow: "auto",
                  border: "1px solid gray",
                }}
              >
                <AgGridReact
                  key={currentTeamId * 1000} // update the key to force a new instance
                  columnDefs={columnDefs}
                  rowData={rowData}
                  onGridReady={onGridReady}
                  rowSelection={"single"}
                  onRowDoubleClicked={onRowDoubleClicked}
                  animateRows={true}
                  onSelectionChanged={onSelectionChanged}
                  onPinnedRowDataChanged={onPinnedRowDataChanged}
                  onColumnMoved={onColumnChanged}
                  onColumnVisible={onColumnChanged}
                  onColumnResized={onColumnChanged}
                  onFilterChanged={onFilterChanged}
                  alwaysShowHorizontalScroll={true}
                  onFirstDataRendered={onFirstDataRendered}
                  gridOptions={gridOptions}
                  onGridPreDestroyed={onGridPreDestroyed}
                  groupDisplayType={"multipleColumns"}
                  groupDefaultExpanded={2}
                  autoGroupColumnDef={{
                    headerValueGetter: (params: HeaderValueGetterParams) =>
                      `${params.colDef.headerName}(Group Column)`,
                    minWidth: 200,
                    filter: "agTextColumnFilter",
                    cellRendererParams: {
                      suppressCount: false,
                    },
                  }}
                  suppressAggFuncInHeader={true}
                  groupHideOpenParents={false}
                  getRowStyle={(params) => {
                    const is_manual_weblab = params.data?.is_manual_weblab;
                    if (is_manual_weblab !== undefined && is_manual_weblab) {
                      return { backgroundColor: "#f5f5dc" };
                    }
                    return undefined;
                  }}
                />
              </div>
              <AdminDialog
                isOpen={showAdminDialog}
                metaData={metadata!}
                onClose={handleAdminClose}
                configs={configs}
                initialData={teamSettings}
                currentUserName={currentUserName}
                currentUserId={currentUserId}
                currentTeamId={currentTeamId}
                ids={weblabIds}
              />
              <ErrorModal error={error} onClose={() => setError(null)} />
              <MessageModal message={message} />
            </div>
          </Resizable>
        </Resizable>
        {showHelp && <HelpCenter category={"review"} />}
      </div>
    )
  );
};

export default ReviewGridComponent;
